/**
 * Admin: per-Service-Group crisis-resource configuration.
 *
 * Shape on disk:
 *   crisisResources = {
 *     youthThresholdYears,
 *     defaultLanguage,
 *     audiences: { [audienceKey]: { i18n: { [lang]: bundle } } }
 *   }
 *   bundle = { label, primary {name,number,detail,url}, secondary {...}, extra, emergencyText }
 *
 * The validator's SI-recovery text and the in-app crisis banner both read
 * from this config via crisisResourcesService.resolveServiceGroupBundle.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, TextField, Button, Alert, CircularProgress,
  IconButton, Tabs, Tab, MenuItem, Paper, Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import api from '../../../services/api';

const SUPPORTED_LANGUAGES = [{ code: 'en', label: 'English' }];
const KNOWN_AUDIENCES = ['default', 'youth'];

function emptyBundle() {
  return {
    label: '',
    primary:   { name: '', number: '', detail: '', url: '' },
    secondary: { name: '', number: '', detail: '', url: '' },
    extra: '',
    emergencyText: ''
  };
}

export default function ServiceGroupCrisisResources({ groupId, groupName, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [youthThresholdYears, setYouthThresholdYears] = useState(18);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [audiences, setAudiences] = useState({});
  const [activeAudience, setActiveAudience] = useState('default');
  const [activeLanguage, setActiveLanguage] = useState('en');

  // Load -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/crisis-resources-admin/admin/service-groups/${groupId}`, token);
        if (cancelled) return;
        const cr = res?.data?.crisisResources || {};
        setYouthThresholdYears(cr.youthThresholdYears ?? 18);
        setDefaultLanguage(cr.defaultLanguage || 'en');
        // Ensure both first-class audience keys exist in form state — admins
        // can leave one empty / undefined and it just won't be saved without
        // a complete bundle.
        const a = { ...(cr.audiences || {}) };
        if (!a.default) a.default = { i18n: { en: emptyBundle() } };
        if (!a.youth)   a.youth   = { i18n: { en: emptyBundle() } };
        setAudiences(a);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Could not load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, token]);

  const audienceKeys = useMemo(() => Object.keys(audiences), [audiences]);

  function updateBundle(audienceKey, lang, patch) {
    setAudiences(prev => {
      const cur = prev[audienceKey] || { i18n: {} };
      const bundle = { ...(cur.i18n?.[lang] || emptyBundle()), ...patch };
      return {
        ...prev,
        [audienceKey]: { ...cur, i18n: { ...(cur.i18n || {}), [lang]: bundle } }
      };
    });
  }

  function updateContact(audienceKey, lang, slot /* 'primary' | 'secondary' */, patch) {
    setAudiences(prev => {
      const cur = prev[audienceKey] || { i18n: {} };
      const bundle = cur.i18n?.[lang] || emptyBundle();
      const next = { ...bundle, [slot]: { ...bundle[slot], ...patch } };
      return {
        ...prev,
        [audienceKey]: { ...cur, i18n: { ...(cur.i18n || {}), [lang]: next } }
      };
    });
  }

  function addAudience() {
    const raw = window.prompt('New audience key (e.g. "perinatal", "veteran" — lowercase, ≤33 chars):', '');
    if (!raw) return;
    const key = raw.trim().toLowerCase();
    if (!/^[a-z_][a-z0-9_-]{0,32}$/.test(key)) {
      setError('Invalid audience key (lowercase letters, digits, underscores, hyphens; ≤33 chars).');
      return;
    }
    if (audiences[key]) {
      setActiveAudience(key);
      return;
    }
    setAudiences(prev => ({ ...prev, [key]: { i18n: { en: emptyBundle() } } }));
    setActiveAudience(key);
    setActiveLanguage('en');
  }

  function removeAudience(key) {
    if (KNOWN_AUDIENCES.includes(key)) {
      setError(`Can't remove the "${key}" audience — clear its fields if you want it ignored.`);
      return;
    }
    setAudiences(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (activeAudience === key) setActiveAudience('default');
  }

  function addLanguage(audienceKey) {
    const raw = window.prompt('Language code (ISO 639-1, e.g. "en", "fr"):', 'en');
    if (!raw) return;
    const code = raw.trim().toLowerCase();
    if (!/^[a-z]{2}$/.test(code)) {
      setError('Language code must be exactly 2 lowercase letters.');
      return;
    }
    const cur = audiences[audienceKey] || { i18n: {} };
    if (cur.i18n?.[code]) {
      setActiveLanguage(code);
      return;
    }
    setAudiences(prev => ({
      ...prev,
      [audienceKey]: { ...cur, i18n: { ...(cur.i18n || {}), [code]: emptyBundle() } }
    }));
    setActiveLanguage(code);
  }

  // Drop audiences with no complete bundle so we never save an invalid map.
  function pruneForSave(map) {
    const out = {};
    for (const [aud, cfg] of Object.entries(map || {})) {
      const i18n = {};
      for (const [lang, bundle] of Object.entries(cfg?.i18n || {})) {
        const p = bundle?.primary || {};
        const s = bundle?.secondary || {};
        if (p.name && p.number && s.name && s.number) {
          i18n[lang] = {
            label: bundle.label || '',
            primary:   { name: p.name, number: p.number, ...(p.detail ? { detail: p.detail } : {}), ...(p.url ? { url: p.url } : {}) },
            secondary: { name: s.name, number: s.number, ...(s.detail ? { detail: s.detail } : {}), ...(s.url ? { url: s.url } : {}) },
            ...(bundle.extra ? { extra: bundle.extra } : {}),
            ...(bundle.emergencyText ? { emergencyText: bundle.emergencyText } : {})
          };
        }
      }
      if (Object.keys(i18n).length > 0) out[aud] = { i18n };
    }
    return out;
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const cleaned = pruneForSave(audiences);
      if (!cleaned.default) {
        throw new Error('The "default" audience must have at least one complete language bundle (name + number on both primary and secondary).');
      }
      await api.patch(
        `/crisis-resources-admin/admin/service-groups/${groupId}`,
        {
          youthThresholdYears: Number(youthThresholdYears),
          defaultLanguage,
          audiences: cleaned
        },
        token
      );
      onClose?.({ saved: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const activeBundle = audiences[activeAudience]?.i18n?.[activeLanguage] || emptyBundle();
  const audienceLanguageKeys = Object.keys(audiences[activeAudience]?.i18n || {});

  return (
    <Box sx={{ p: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Crisis resources — {groupName || 'service group'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These contacts are surfaced by the in-app crisis banner and by the
        safety guard's recovery message after a dismissive AI reply. Each
        audience can carry one bundle per language; <strong>default</strong>{' '}
        is the universal fallback, <strong>youth</strong> takes priority for
        users below the threshold age.
      </Typography>

      {/* Top-level controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Youth threshold (years)"
            type="number"
            size="small"
            inputProps={{ min: 0, max: 25 }}
            value={youthThresholdYears}
            onChange={(e) => setYouthThresholdYears(e.target.value)}
            sx={{ maxWidth: 200 }}
          />
          <TextField
            label="Default language"
            select
            size="small"
            value={defaultLanguage}
            onChange={(e) => setDefaultLanguage(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {SUPPORTED_LANGUAGES.map(l => (
              <MenuItem key={l.code} value={l.code}>{l.code} — {l.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {/* Audience selector */}
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Tabs
          value={activeAudience}
          onChange={(_, v) => { setActiveAudience(v); setActiveLanguage(Object.keys(audiences[v]?.i18n || {})[0] || 'en'); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {audienceKeys.map(k => (
            <Tab
              key={k}
              value={k}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>{k}</span>
                  {!KNOWN_AUDIENCES.includes(k) && (
                    <Tooltip title="Remove this audience">
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); removeAudience(k); }}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="Add a new audience (perinatal, veteran, etc.)">
          <IconButton size="small" onClick={addAudience} sx={{ ml: 1 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Language tabs for the active audience */}
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Tabs
          value={audienceLanguageKeys.includes(activeLanguage) ? activeLanguage : audienceLanguageKeys[0] || 'en'}
          onChange={(_, v) => setActiveLanguage(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {audienceLanguageKeys.map(lang => (
            <Tab key={lang} value={lang} label={lang} />
          ))}
        </Tabs>
        <Tooltip title="Add another language bundle for this audience">
          <IconButton size="small" onClick={() => addLanguage(activeAudience)} sx={{ ml: 1 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Bundle editor */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {activeAudience} / {activeLanguage}
        </Typography>

        <TextField
          label="Region label (shown in audit logs / therapist UI)"
          size="small" fullWidth sx={{ mb: 2 }}
          placeholder="e.g. UK, Ireland, Greater Toronto Area"
          value={activeBundle.label || ''}
          onChange={(e) => updateBundle(activeAudience, activeLanguage, { label: e.target.value })}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>Primary contact</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField label="Name *" size="small" sx={{ flex: 2 }}
            value={activeBundle.primary?.name || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'primary', { name: e.target.value })}
          />
          <TextField label="Number *" size="small" sx={{ flex: 1 }}
            value={activeBundle.primary?.number || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'primary', { number: e.target.value })}
          />
          <TextField label="Detail" size="small" sx={{ flex: 1 }}
            placeholder="free, 24/7"
            value={activeBundle.primary?.detail || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'primary', { detail: e.target.value })}
          />
          <TextField label="URL" size="small" sx={{ flex: 2 }}
            value={activeBundle.primary?.url || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'primary', { url: e.target.value })}
          />
        </Stack>

        <Typography variant="subtitle2">Secondary contact</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField label="Name *" size="small" sx={{ flex: 2 }}
            value={activeBundle.secondary?.name || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'secondary', { name: e.target.value })}
          />
          <TextField label="Number *" size="small" sx={{ flex: 1 }}
            value={activeBundle.secondary?.number || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'secondary', { number: e.target.value })}
          />
          <TextField label="Detail" size="small" sx={{ flex: 1 }}
            value={activeBundle.secondary?.detail || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'secondary', { detail: e.target.value })}
          />
          <TextField label="URL" size="small" sx={{ flex: 2 }}
            value={activeBundle.secondary?.url || ''}
            onChange={(e) => updateContact(activeAudience, activeLanguage, 'secondary', { url: e.target.value })}
          />
        </Stack>

        <TextField
          label="Additional guidance ('extra')"
          size="small" fullWidth multiline minRows={1} sx={{ mb: 2 }}
          placeholder="e.g. Or contact your GP / GP out-of-hours service"
          value={activeBundle.extra || ''}
          onChange={(e) => updateBundle(activeAudience, activeLanguage, { extra: e.target.value })}
        />

        <TextField
          label="Emergency line (shown for immediate-danger cases)"
          size="small" fullWidth multiline minRows={1}
          placeholder="If life is in immediate danger, call 999."
          value={activeBundle.emergencyText || ''}
          onChange={(e) => updateBundle(activeAudience, activeLanguage, { emergencyText: e.target.value })}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          * = required. Bundles missing a required field are silently dropped on save.
        </Typography>
      </Paper>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
        <Button onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Box>
  );
}
